import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../App';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Progress } from '../../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import {
  Home, Users, Building, Gavel, BarChart3, Settings, LogOut, Menu,
  Check, X, Eye, UserCheck, UserX, ChevronRight, FileText, Search,
  Shield, AlertCircle, CheckCircle, Clock, User, XCircle, Loader2, AlertTriangle
} from 'lucide-react';
import { DialogDescription } from '../../components/ui/dialog';
import AuditLogsPage from './AuditLogsPage';
import PrivateListingScout from './PrivateListingScout';

// Sidebar Component
const Sidebar = ({ isOpen, setIsOpen }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();

  const menuItems = [
    { path: '/admin', icon: Home, label: 'Dashboard', exact: true },
    { path: '/admin/agenti', icon: UserCheck, label: 'Gestione Agenti' },
    { path: '/admin/acquirenti', icon: Users, label: 'Gestione Acquirenti' },
    { path: '/admin/proprietari', icon: User, label: 'Gestione Proprietari' },
    { path: '/admin/immobili-agenti', icon: Building, label: 'Immobili Agenti' },
    { path: '/admin/immobili-proprietari', icon: Home, label: 'Immobili Proprietari' },
    { path: '/admin/scout', icon: Search, label: 'Private Listing Scout' },
    { path: '/admin/gare', icon: Gavel, label: 'Monitoraggio Gare' },
    { path: '/admin/analytics', icon: BarChart3, label: 'Analytics' },
    { path: '/admin/sicurezza', icon: Shield, label: 'Sicurezza & Audit' },
  ];

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setIsOpen(false)} />
      )}

      <aside className={`fixed top-0 left-0 h-full w-64 bg-[#0071e3] z-50 transform transition-transform duration-300 lg:translate-x-0 ${isOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          <div className="p-6 border-b border-white/10">
            <Link to="/" className="flex items-center space-x-2">
              <img 
                src="/weqasa-icon.png" 
                alt="WEQASA" 
                className="w-10 h-10 rounded-lg object-contain"
              />
              <div>
                <span className="text-xl font-bold text-white">WEQASA</span>
                <p className="text-xs text-gray-300">Admin Panel</p>
              </div>
            </Link>
          </div>

          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {menuItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setIsOpen(false)}
                className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                  isActive(item.path, item.exact)
                    ? 'bg-white/20 text-white'
                    : 'text-gray-300 hover:bg-white/10 hover:text-white'
                }`}
              >
                <item.icon className="h-5 w-5" />
                <span>{item.label}</span>
              </Link>
            ))}
          </nav>

          <div className="p-4 border-t border-white/10">
            <button
              onClick={handleLogout}
              className="flex items-center space-x-3 px-4 py-3 w-full text-red-300 hover:bg-red-500/20 rounded-lg transition-colors"
            >
              <LogOut className="h-5 w-5" />
              <span>Esci</span>
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

// Dashboard Overview
const AdminOverview = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get('/admin/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="text-center py-8">Caricamento...</div>;

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <h1 className="text-2xl font-bold text-gray-900 ">Dashboard Admin</h1>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white p-6 rounded-lg border border-gray-100">
          <div className="text-3xl font-bold text-[#0071e3]">{stats?.total_agents || 0}</div>
          <div className="text-sm text-gray-500">Agenti Totali</div>
          {stats?.pending_agents > 0 && (
            <Badge className="badge-pending mt-2">{stats.pending_agents} in attesa</Badge>
          )}
        </div>
        <div className="bg-white p-6 rounded-lg border border-gray-100">
          <div className="text-3xl font-bold text-[#0071e3]">{stats?.total_buyers || 0}</div>
          <div className="text-sm text-gray-500">Acquirenti</div>
        </div>
        <div className="bg-white p-6 rounded-lg border border-gray-100">
          <div className="text-3xl font-bold text-[#0071e3]">{stats?.total_properties || 0}</div>
          <div className="text-sm text-gray-500">Immobili</div>
        </div>
        <div className="bg-white p-6 rounded-lg border border-gray-100">
          <div className="text-3xl font-bold text-green-600">{stats?.active_auctions || 0}</div>
          <div className="text-sm text-gray-500">Aste Attive</div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg border border-gray-100">
          <div className="p-6 border-b border-gray-100 flex justify-between items-center">
            <h2 className="font-semibold text-lg">Statistiche Aste</h2>
          </div>
          <div className="p-6 space-y-4">
            <div className="flex justify-between">
              <span className="text-gray-500">Aste Programmate</span>
              <span className="font-semibold">{stats?.scheduled_auctions || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Aste Attive</span>
              <span className="font-semibold text-green-600">{stats?.active_auctions || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Offerte Totali</span>
              <span className="font-semibold">{stats?.total_bids || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Visite Prenotate</span>
              <span className="font-semibold">{stats?.total_visits || 0}</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-100">
          <div className="p-6 border-b border-gray-100">
            <h2 className="font-semibold text-lg">Azioni Rapide</h2>
          </div>
          <div className="p-6 space-y-3">
            <Link to="/admin/agenti" className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition">
              <div className="flex items-center">
                <UserCheck className="h-5 w-5 text-[#0071e3] mr-3" />
                <span>Gestisci Agenti</span>
              </div>
              <ChevronRight className="h-5 w-5 text-gray-400" />
            </Link>
            <Link to="/admin/acquirenti" className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition">
              <div className="flex items-center">
                <Users className="h-5 w-5 text-[#0071e3] mr-3" />
                <span>Gestisci Acquirenti</span>
              </div>
              <ChevronRight className="h-5 w-5 text-gray-400" />
            </Link>
            <Link to="/admin/immobili" className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition">
              <div className="flex items-center">
                <Building className="h-5 w-5 text-[#0071e3] mr-3" />
                <span>Modera Immobili</span>
              </div>
              <ChevronRight className="h-5 w-5 text-gray-400" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

// Agents Management
const AgentsManagementPage = () => {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAgents();
  }, []);

  const fetchAgents = async () => {
    try {
      const response = await axios.get('/admin/agents');
      setAgents(response.data);
    } catch (error) {
      console.error('Error fetching agents:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (userId, status) => {
    try {
      await axios.put(`/admin/agents/${userId}/status?status=${status}`);
      toast.success('Stato aggiornato');
      fetchAgents();
    } catch (error) {
      toast.error('Errore nell\'aggiornamento');
    }
  };

  if (loading) return <div className="text-center py-8">Caricamento...</div>;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900 ">Gestione Agenti</h1>

      {agents.length > 0 ? (
        <div className="bg-white rounded-lg border border-gray-100 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Agenzia</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contatto</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">P.IVA / REA</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stato</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {agents.map((agent) => (
                <tr key={agent.user_id}>
                  <td className="px-6 py-4">
                    <div className="font-medium text-gray-900">{agent.company_name}</div>
                    <div className="text-sm text-gray-500">{agent.first_name} {agent.last_name}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm">{agent.email}</div>
                    <div className="text-sm text-gray-500">{agent.phone}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm">{agent.vat_number}</div>
                    <div className="text-sm text-gray-500">{agent.rea_number}</div>
                  </td>
                  <td className="px-6 py-4">
                    <Badge className={
                      agent.verification_status === 'approved' ? 'badge-active' :
                      agent.verification_status === 'pending_verification' ? 'badge-pending' :
                      agent.verification_status === 'suspended' ? 'badge-closed' : 'badge-pending'
                    }>
                      {agent.verification_status}
                    </Badge>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex space-x-2">
                      {agent.verification_status === 'pending_verification' && (
                        <Button size="sm" onClick={() => updateStatus(agent.user_id, 'approved')} className="bg-green-600 hover:bg-green-700 text-white">
                          <Check className="h-4 w-4" />
                        </Button>
                      )}
                      {agent.verification_status === 'approved' && (
                        <Button size="sm" variant="outline" onClick={() => updateStatus(agent.user_id, 'suspended')} className="text-red-600">
                          <UserX className="h-4 w-4" />
                        </Button>
                      )}
                      {agent.verification_status === 'suspended' && (
                        <Button size="sm" variant="outline" onClick={() => updateStatus(agent.user_id, 'approved')}>
                          <UserCheck className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-100 p-8 text-center">
          <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">Nessun agente registrato</p>
        </div>
      )}
    </div>
  );
};

// Buyers Management
const BuyersManagementPage = () => {
  const [buyers, setBuyers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBuyers();
  }, []);

  const fetchBuyers = async () => {
    try {
      const response = await axios.get('/admin/buyers');
      setBuyers(response.data);
    } catch (error) {
      console.error('Error fetching buyers:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="text-center py-8">Caricamento...</div>;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900 ">Gestione Acquirenti</h1>

      {buyers.length > 0 ? (
        <div className="bg-white rounded-lg border border-gray-100 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nome</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contatto</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Codice Fiscale</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Verifiche</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Registrato</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {buyers.map((buyer) => (
                <tr key={buyer.user_id}>
                  <td className="px-6 py-4">
                    <div className="font-medium text-gray-900">{buyer.first_name} {buyer.last_name}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm">{buyer.email}</div>
                    <div className="text-sm text-gray-500">{buyer.phone || 'N/D'}</div>
                  </td>
                  <td className="px-6 py-4 text-sm">{buyer.tax_code || 'N/D'}</td>
                  <td className="px-6 py-4">
                    <div className="flex space-x-2">
                      <Badge className={buyer.email_verified ? 'badge-active' : 'badge-pending'}>
                        Email
                      </Badge>
                      <Badge className={buyer.phone_verified ? 'badge-active' : 'badge-pending'}>
                        Tel
                      </Badge>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {buyer.created_at ? format(new Date(buyer.created_at), 'dd/MM/yyyy', { locale: it }) : 'N/D'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-100 p-8 text-center">
          <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">Nessun acquirente registrato</p>
        </div>
      )}
    </div>
  );
};

// Properties Management
const PropertiesManagementPage = () => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingProperty, setEditingProperty] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [editForm, setEditForm] = useState({
    title: '',
    description: '',
    property_type: '',
    city: '',
    area: '',
    address: '',
    sqm: '',
    rooms: '',
    bathrooms: '',
    floor: '',
    auction_start_price: '',
    auction_status: ''
  });

  useEffect(() => {
    fetchProperties();
  }, []);

  const fetchProperties = async () => {
    try {
      const response = await axios.get('/admin/properties');
      setProperties(response.data);
    } catch (error) {
      console.error('Error fetching properties:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (propertyId, status) => {
    try {
      await axios.put(`/admin/properties/${propertyId}/status?status=${status}`);
      toast.success('Stato aggiornato');
      fetchProperties();
    } catch (error) {
      toast.error('Errore nell\'aggiornamento');
    }
  };

  const handleEdit = (property) => {
    setEditingProperty(property);
    setEditForm({
      title: property.title || '',
      description: property.description || '',
      property_type: property.property_type || '',
      city: property.city || '',
      area: property.area || '',
      address: property.address || '',
      sqm: property.sqm || '',
      rooms: property.rooms || '',
      bathrooms: property.bathrooms || '',
      floor: property.floor || '',
      auction_start_price: property.auction_start_price || '',
      auction_status: property.auction_status || ''
    });
    setShowEditDialog(true);
  };

  const handleSaveEdit = async () => {
    if (!editingProperty) return;
    setSaving(true);
    try {
      // Convert numeric fields
      const dataToSend = {
        ...editForm,
        sqm: editForm.sqm ? parseInt(editForm.sqm) : null,
        rooms: editForm.rooms ? parseInt(editForm.rooms) : null,
        bathrooms: editForm.bathrooms ? parseInt(editForm.bathrooms) : null,
        auction_start_price: editForm.auction_start_price ? parseFloat(editForm.auction_start_price) : null
      };
      
      await axios.patch(`/admin/property/${editingProperty.property_id}/fix`, dataToSend);
      toast.success('Immobile aggiornato con successo');
      setShowEditDialog(false);
      setEditingProperty(null);
      fetchProperties();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'aggiornamento');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (propertyId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo immobile? Questa azione è irreversibile.')) return;
    try {
      await axios.delete(`/admin/property/${propertyId}`);
      toast.success('Immobile eliminato');
      fetchProperties();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'eliminazione');
    }
  };

  const formatPrice = (price) => {
    if (!price) return 'N/D';
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0 }).format(price);
  };

  const filteredProperties = properties.filter(prop => 
    prop.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    prop.city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    prop.property_id?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const propertyTypes = [
    'Appartamento', 'Villa', 'Casa Indipendente', 'Attico', 'Mansarda', 
    'Loft', 'Bilocale', 'Trilocale', 'Quadrilocale', 'Rustico', 
    'Palazzo', 'Ufficio', 'Negozio', 'Box', 'Terreno'
  ];

  const auctionStatuses = [
    { value: 'draft', label: 'Bozza' },
    { value: 'published', label: 'Pubblicato' },
    { value: 'scheduled', label: 'Programmato' },
    { value: 'active', label: 'Attivo' },
    { value: 'completed', label: 'Completato' },
    { value: 'cancelled', label: 'Annullato' }
  ];

  if (loading) return <div className="text-center py-8"><Loader2 className="h-8 w-8 animate-spin mx-auto text-[#0071e3]" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-2xl font-bold text-gray-900">Gestione Immobili</h1>
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Cerca per titolo, città o ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      <div className="text-sm text-gray-500 mb-4">
        {filteredProperties.length} immobili trovati
      </div>

      {filteredProperties.length > 0 ? (
        <div className="bg-white rounded-lg border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Immobile</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Località</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Prezzo</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stato</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Azioni</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredProperties.map((prop) => (
                  <tr key={prop.property_id} className="hover:bg-gray-50">
                    <td className="px-4 py-4">
                      <div className="font-medium text-gray-900">{prop.title}</div>
                      <div className="text-sm text-gray-500">{prop.property_type} • {prop.sqm} m² • {prop.rooms} locali</div>
                      <div className="text-xs text-gray-400 mt-1">ID: {prop.property_id}</div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-sm text-gray-900">{prop.city}</div>
                      <div className="text-xs text-gray-500">{prop.area}</div>
                    </td>
                    <td className="px-4 py-4 text-sm font-medium text-gray-900">{formatPrice(prop.auction_start_price)}</td>
                    <td className="px-4 py-4">
                      <Badge className={
                        prop.auction_status === 'active' ? 'bg-green-100 text-green-800' :
                        prop.auction_status === 'scheduled' ? 'bg-blue-100 text-blue-800' :
                        prop.auction_status === 'published' ? 'bg-yellow-100 text-yellow-800' :
                        prop.auction_status === 'completed' ? 'bg-gray-100 text-gray-800' :
                        prop.auction_status === 'cancelled' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-600'
                      }>
                        {prop.auction_status || 'Bozza'}
                      </Badge>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex flex-wrap gap-2">
                        <Button size="sm" variant="outline" onClick={() => handleEdit(prop)} title="Modifica">
                          <FileText className="h-4 w-4" />
                        </Button>
                        <Link to={`/immobili/${prop.property_id}`} target="_blank">
                          <Button size="sm" variant="outline" title="Visualizza">
                            <Eye className="h-4 w-4" />
                          </Button>
                        </Link>
                        {prop.auction_status === 'draft' && (
                          <Button size="sm" onClick={() => updateStatus(prop.property_id, 'published')} className="bg-green-600 hover:bg-green-700 text-white">
                            Pubblica
                          </Button>
                        )}
                        <Button size="sm" variant="outline" onClick={() => handleDelete(prop.property_id)} className="text-red-600 hover:bg-red-50" title="Elimina">
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-100 p-8 text-center">
          <Building className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">Nessun immobile trovato</p>
        </div>
      )}

      {/* Edit Property Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Modifica Immobile</DialogTitle>
            <DialogDescription>
              Modifica i dati dell'immobile. Tutti i campi sono modificabili.
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
            {/* Titolo */}
            <div className="md:col-span-2">
              <Label htmlFor="title">Titolo *</Label>
              <Input
                id="title"
                value={editForm.title}
                onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                placeholder="Es: Trilocale Luminoso - Via Roma"
              />
            </div>

            {/* Descrizione */}
            <div className="md:col-span-2">
              <Label htmlFor="description">Descrizione</Label>
              <Textarea
                id="description"
                value={editForm.description}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                placeholder="Descrizione dell'immobile..."
                rows={4}
              />
            </div>

            {/* Tipologia */}
            <div>
              <Label htmlFor="property_type">Tipologia</Label>
              <Select value={editForm.property_type} onValueChange={(value) => setEditForm({ ...editForm, property_type: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleziona tipologia" />
                </SelectTrigger>
                <SelectContent>
                  {propertyTypes.map((type) => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Stato Asta */}
            <div>
              <Label htmlFor="auction_status">Stato</Label>
              <Select value={editForm.auction_status} onValueChange={(value) => setEditForm({ ...editForm, auction_status: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleziona stato" />
                </SelectTrigger>
                <SelectContent>
                  {auctionStatuses.map((status) => (
                    <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Città */}
            <div>
              <Label htmlFor="city">Città</Label>
              <Input
                id="city"
                value={editForm.city}
                onChange={(e) => setEditForm({ ...editForm, city: e.target.value })}
                placeholder="Es: Roma"
              />
            </div>

            {/* Zona */}
            <div>
              <Label htmlFor="area">Zona / Quartiere</Label>
              <Input
                id="area"
                value={editForm.area}
                onChange={(e) => setEditForm({ ...editForm, area: e.target.value })}
                placeholder="Es: Trastevere"
              />
            </div>

            {/* Indirizzo */}
            <div className="md:col-span-2">
              <Label htmlFor="address">Indirizzo</Label>
              <Input
                id="address"
                value={editForm.address}
                onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                placeholder="Es: Via Roma 42"
              />
            </div>

            {/* Metratura */}
            <div>
              <Label htmlFor="sqm">Superficie (m²)</Label>
              <Input
                id="sqm"
                type="number"
                value={editForm.sqm}
                onChange={(e) => setEditForm({ ...editForm, sqm: e.target.value })}
                placeholder="Es: 85"
              />
            </div>

            {/* Locali */}
            <div>
              <Label htmlFor="rooms">Locali</Label>
              <Input
                id="rooms"
                type="number"
                value={editForm.rooms}
                onChange={(e) => setEditForm({ ...editForm, rooms: e.target.value })}
                placeholder="Es: 3"
              />
            </div>

            {/* Bagni */}
            <div>
              <Label htmlFor="bathrooms">Bagni</Label>
              <Input
                id="bathrooms"
                type="number"
                value={editForm.bathrooms}
                onChange={(e) => setEditForm({ ...editForm, bathrooms: e.target.value })}
                placeholder="Es: 2"
              />
            </div>

            {/* Piano */}
            <div>
              <Label htmlFor="floor">Piano</Label>
              <Input
                id="floor"
                value={editForm.floor}
                onChange={(e) => setEditForm({ ...editForm, floor: e.target.value })}
                placeholder="Es: 3° piano"
              />
            </div>

            {/* Prezzo */}
            <div className="md:col-span-2">
              <Label htmlFor="auction_start_price">Prezzo Base (€)</Label>
              <Input
                id="auction_start_price"
                type="number"
                value={editForm.auction_start_price}
                onChange={(e) => setEditForm({ ...editForm, auction_start_price: e.target.value })}
                placeholder="Es: 250000"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Annulla
            </Button>
            <Button onClick={handleSaveEdit} disabled={saving} className="bg-[#0071e3] hover:bg-[#0077ED]">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Salva Modifiche
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Seller Properties Management - Immobili Proprietari
const SellerPropertiesManagementPage = () => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectDialog, setShowRejectDialog] = useState(false);

  useEffect(() => {
    fetchSellerProperties();
  }, []);

  const fetchSellerProperties = async () => {
    try {
      const response = await axios.get('/admin/seller-properties');
      setProperties(response.data || []);
    } catch (error) {
      console.error('Error fetching seller properties:', error);
      toast.error('Errore nel caricamento degli immobili');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (propertyId) => {
    setActionLoading(true);
    try {
      await axios.put(`/admin/seller-properties/${propertyId}/approve`);
      toast.success('Immobile approvato! Il proprietario è stato notificato.');
      fetchSellerProperties();
      setShowDetailDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'approvazione');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selectedProperty || !rejectionReason.trim()) {
      toast.error('Inserisci il motivo del rifiuto');
      return;
    }
    setActionLoading(true);
    try {
      await axios.put(`/admin/seller-properties/${selectedProperty.property_id}/reject`, {
        reason: rejectionReason
      });
      toast.success('Immobile rifiutato. Il proprietario è stato notificato.');
      fetchSellerProperties();
      setShowRejectDialog(false);
      setShowDetailDialog(false);
      setRejectionReason('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel rifiuto');
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/D';
    return format(new Date(dateString), 'dd MMM yyyy, HH:mm', { locale: it });
  };

  const formatPrice = (price) => {
    if (!price) return 'N/D';
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0 }).format(price);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'draft': { label: 'Bozza', className: 'bg-gray-100 text-gray-800' },
      'pending_review': { label: 'In Revisione', className: 'bg-yellow-100 text-yellow-800' },
      'approved': { label: 'Approvato', className: 'bg-green-100 text-green-800' },
      'rejected': { label: 'Rifiutato', className: 'bg-red-100 text-red-800' },
      'mandate_signed': { label: 'Mandato Firmato', className: 'bg-blue-100 text-blue-800' },
      'auction_published': { label: 'In Asta', className: 'bg-purple-100 text-purple-800' },
      'sold': { label: 'Venduto', className: 'bg-indigo-100 text-indigo-800' }
    };
    const config = statusConfig[status] || { label: status, className: 'bg-gray-100 text-gray-800' };
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  const filteredProperties = properties.filter(prop => {
    const matchesSearch = 
      prop.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      prop.city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      prop.property_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      prop.seller_name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || prop.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const pendingCount = properties.filter(p => p.status === 'pending_review').length;

  if (loading) return <div className="text-center py-8"><Loader2 className="h-8 w-8 animate-spin mx-auto text-[#0071e3]" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Immobili Proprietari</h1>
          <p className="text-sm text-gray-500 mt-1">
            Gestisci e verifica gli immobili inseriti dai proprietari
          </p>
        </div>
        {pendingCount > 0 && (
          <div className="flex items-center space-x-2 bg-yellow-50 border border-yellow-200 rounded-lg px-4 py-2">
            <AlertCircle className="h-5 w-5 text-yellow-600" />
            <span className="text-yellow-800 font-medium">{pendingCount} immobili in attesa di revisione</span>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Cerca per titolo, città, ID o proprietario..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-48">
            <SelectValue placeholder="Filtra per stato" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti gli stati</SelectItem>
            <SelectItem value="pending_review">In Revisione</SelectItem>
            <SelectItem value="approved">Approvati</SelectItem>
            <SelectItem value="rejected">Rifiutati</SelectItem>
            <SelectItem value="draft">Bozze</SelectItem>
            <SelectItem value="mandate_signed">Mandato Firmato</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg border p-4">
          <div className="text-2xl font-bold text-gray-900">{properties.length}</div>
          <div className="text-sm text-gray-500">Totale Immobili</div>
        </div>
        <div className="bg-yellow-50 rounded-lg border border-yellow-200 p-4">
          <div className="text-2xl font-bold text-yellow-700">{pendingCount}</div>
          <div className="text-sm text-yellow-600">In Revisione</div>
        </div>
        <div className="bg-green-50 rounded-lg border border-green-200 p-4">
          <div className="text-2xl font-bold text-green-700">{properties.filter(p => p.status === 'approved').length}</div>
          <div className="text-sm text-green-600">Approvati</div>
        </div>
        <div className="bg-red-50 rounded-lg border border-red-200 p-4">
          <div className="text-2xl font-bold text-red-700">{properties.filter(p => p.status === 'rejected').length}</div>
          <div className="text-sm text-red-600">Rifiutati</div>
        </div>
      </div>

      {/* Properties Table */}
      {filteredProperties.length > 0 ? (
        <div className="bg-white rounded-lg border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Immobile</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Proprietario</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data Inserimento</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stato</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Azioni</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredProperties.map((prop) => (
                  <tr key={prop.property_id} className={`hover:bg-gray-50 ${prop.status === 'pending_review' ? 'bg-yellow-50/50' : ''}`}>
                    <td className="px-4 py-4">
                      <div className="font-medium text-gray-900">{prop.title || 'Senza titolo'}</div>
                      <div className="text-sm text-gray-500">
                        {prop.property_type} • {prop.sqm} m² • {prop.rooms} locali
                      </div>
                      <div className="text-sm text-gray-500">{prop.city}, {prop.area}</div>
                      <div className="text-xs text-gray-400 mt-1">ID: {prop.property_id}</div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-sm text-gray-900">{prop.seller_name || 'N/D'}</div>
                      <div className="text-xs text-gray-500">{prop.seller_email}</div>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-600">
                      {formatDate(prop.created_at)}
                    </td>
                    <td className="px-4 py-4">
                      {getStatusBadge(prop.status)}
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex flex-wrap gap-2">
                        <Button 
                          size="sm" 
                          variant="outline" 
                          onClick={() => { setSelectedProperty(prop); setShowDetailDialog(true); }}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          Dettagli
                        </Button>
                        {prop.status === 'pending_review' && (
                          <>
                            <Button 
                              size="sm" 
                              className="bg-green-600 hover:bg-green-700 text-white"
                              onClick={() => handleApprove(prop.property_id)}
                              disabled={actionLoading}
                            >
                              <Check className="h-4 w-4 mr-1" />
                              Verifica
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="text-red-600 border-red-300 hover:bg-red-50"
                              onClick={() => { setSelectedProperty(prop); setShowRejectDialog(true); }}
                              disabled={actionLoading}
                            >
                              <X className="h-4 w-4 mr-1" />
                              Rifiuta
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-100 p-8 text-center">
          <Home className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">Nessun immobile trovato</p>
        </div>
      )}

      {/* Property Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Dettagli Immobile</DialogTitle>
            <DialogDescription>
              Verifica i documenti e le informazioni dell'immobile prima di approvarlo
            </DialogDescription>
          </DialogHeader>
          
          {selectedProperty && (
            <div className="space-y-6">
              {/* Property Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium text-gray-500 text-sm">Titolo</h4>
                  <p className="text-gray-900">{selectedProperty.title || 'N/D'}</p>
                </div>
                <div>
                  <h4 className="font-medium text-gray-500 text-sm">Stato</h4>
                  {getStatusBadge(selectedProperty.status)}
                </div>
                <div>
                  <h4 className="font-medium text-gray-500 text-sm">Tipologia</h4>
                  <p className="text-gray-900">{selectedProperty.property_type}</p>
                </div>
                <div>
                  <h4 className="font-medium text-gray-500 text-sm">Superficie</h4>
                  <p className="text-gray-900">{selectedProperty.sqm} m²</p>
                </div>
                <div>
                  <h4 className="font-medium text-gray-500 text-sm">Locali</h4>
                  <p className="text-gray-900">{selectedProperty.rooms} locali, {selectedProperty.bathrooms} bagni</p>
                </div>
                <div>
                  <h4 className="font-medium text-gray-500 text-sm">Piano</h4>
                  <p className="text-gray-900">{selectedProperty.floor || 'N/D'}</p>
                </div>
                <div className="col-span-2">
                  <h4 className="font-medium text-gray-500 text-sm">Indirizzo</h4>
                  <p className="text-gray-900">{selectedProperty.address}, {selectedProperty.city} ({selectedProperty.province})</p>
                </div>
              </div>

              {/* Seller Info */}
              <div className="border-t pt-4">
                <h3 className="font-semibold text-gray-900 mb-3">Informazioni Proprietario</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-medium text-gray-500 text-sm">Nome</h4>
                    <p className="text-gray-900">{selectedProperty.seller_name || 'N/D'}</p>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-500 text-sm">Email</h4>
                    <p className="text-gray-900">{selectedProperty.seller_email || 'N/D'}</p>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-500 text-sm">Telefono</h4>
                    <p className="text-gray-900">{selectedProperty.seller_phone || 'N/D'}</p>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-500 text-sm">Codice Fiscale</h4>
                    <p className="text-gray-900">{selectedProperty.seller_codice_fiscale || 'N/D'}</p>
                  </div>
                </div>
              </div>

              {/* Documents */}
              <div className="border-t pt-4">
                <h3 className="font-semibold text-gray-900 mb-3">Documenti Caricati</h3>
                <div className="space-y-2">
                  {selectedProperty.documents ? (
                    <>
                      {selectedProperty.documents.identity_document && (
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center space-x-2">
                            <CheckCircle className="h-5 w-5 text-green-500" />
                            <span>Documento di Identità</span>
                          </div>
                          {selectedProperty.documents.identity_document.url && (
                            <Button size="sm" variant="outline" onClick={() => window.open(selectedProperty.documents.identity_document.url, '_blank')}>
                              <Eye className="h-4 w-4 mr-1" /> Visualizza
                            </Button>
                          )}
                        </div>
                      )}
                      {selectedProperty.documents.planimetria_catastale && (
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center space-x-2">
                            <CheckCircle className="h-5 w-5 text-green-500" />
                            <span>Planimetria Catastale</span>
                          </div>
                          {selectedProperty.documents.planimetria_catastale.url && (
                            <Button size="sm" variant="outline" onClick={() => window.open(selectedProperty.documents.planimetria_catastale.url, '_blank')}>
                              <Eye className="h-4 w-4 mr-1" /> Visualizza
                            </Button>
                          )}
                        </div>
                      )}
                      {selectedProperty.documents.visura_catastale && (
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center space-x-2">
                            <CheckCircle className="h-5 w-5 text-green-500" />
                            <span>Visura Catastale</span>
                          </div>
                          {selectedProperty.documents.visura_catastale.url && (
                            <Button size="sm" variant="outline" onClick={() => window.open(selectedProperty.documents.visura_catastale.url, '_blank')}>
                              <Eye className="h-4 w-4 mr-1" /> Visualizza
                            </Button>
                          )}
                        </div>
                      )}
                      {selectedProperty.documents.atto_provenienza && (
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center space-x-2">
                            <CheckCircle className="h-5 w-5 text-green-500" />
                            <span>Atto di Provenienza</span>
                          </div>
                          {selectedProperty.documents.atto_provenienza.url && (
                            <Button size="sm" variant="outline" onClick={() => window.open(selectedProperty.documents.atto_provenienza.url, '_blank')}>
                              <Eye className="h-4 w-4 mr-1" /> Visualizza
                            </Button>
                          )}
                        </div>
                      )}
                      {selectedProperty.documents.photos && selectedProperty.documents.photos.length > 0 && (
                        <div className="p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center space-x-2 mb-2">
                            <CheckCircle className="h-5 w-5 text-green-500" />
                            <span>Foto Immobile ({selectedProperty.documents.photos.length})</span>
                          </div>
                          <div className="grid grid-cols-4 gap-2 mt-2">
                            {selectedProperty.documents.photos.slice(0, 8).map((photo, idx) => (
                              <img 
                                key={idx} 
                                src={photo.url} 
                                alt={`Foto ${idx + 1}`}
                                className="w-full h-20 object-cover rounded cursor-pointer hover:opacity-80"
                                onClick={() => window.open(photo.url, '_blank')}
                              />
                            ))}
                            {selectedProperty.documents.photos.length > 8 && (
                              <div className="w-full h-20 bg-gray-200 rounded flex items-center justify-center text-gray-500 text-sm">
                                +{selectedProperty.documents.photos.length - 8} altre
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <p className="text-gray-500 italic">Nessun documento caricato</p>
                  )}
                </div>
              </div>

              {/* Catasto Info */}
              {selectedProperty.catasto && (
                <div className="border-t pt-4">
                  <h3 className="font-semibold text-gray-900 mb-3">Dati Catastali</h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <h4 className="font-medium text-gray-500 text-sm">Foglio</h4>
                      <p className="text-gray-900">{selectedProperty.catasto.foglio || 'N/D'}</p>
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-500 text-sm">Particella</h4>
                      <p className="text-gray-900">{selectedProperty.catasto.particella || 'N/D'}</p>
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-500 text-sm">Subalterno</h4>
                      <p className="text-gray-900">{selectedProperty.catasto.subalterno || 'N/D'}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Actions */}
              {selectedProperty.status === 'pending_review' && (
                <div className="border-t pt-4">
                  <h3 className="font-semibold text-gray-900 mb-3">Azioni</h3>
                  <div className="flex space-x-3">
                    <Button 
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                      onClick={() => handleApprove(selectedProperty.property_id)}
                      disabled={actionLoading}
                    >
                      {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}
                      VERIFICATO - Approva Immobile
                    </Button>
                    <Button 
                      variant="outline"
                      className="flex-1 text-red-600 border-red-300 hover:bg-red-50"
                      onClick={() => setShowRejectDialog(true)}
                      disabled={actionLoading}
                    >
                      <X className="h-4 w-4 mr-2" />
                      NON APPROVATO - Rifiuta
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rifiuta Immobile</DialogTitle>
            <DialogDescription>
              Inserisci il motivo del rifiuto. Il proprietario riceverà una notifica con questa motivazione.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="rejectionReason">Motivo del Rifiuto *</Label>
              <Textarea
                id="rejectionReason"
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Es: Documentazione incompleta, foto non chiare, dati catastali mancanti..."
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowRejectDialog(false); setRejectionReason(''); }}>
              Annulla
            </Button>
            <Button 
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={handleReject}
              disabled={actionLoading || !rejectionReason.trim()}
            >
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Conferma Rifiuto
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Auctions Monitoring
const AuctionsMonitoringPage = () => {
  const navigate = useNavigate();
  const [gare, setGare] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGara, setSelectedGara] = useState(null);
  const [showSuspendDialog, setShowSuspendDialog] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [suspendReason, setSuspendReason] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    fetchGare();
  }, [statusFilter]);

  const fetchGare = async () => {
    try {
      const params = statusFilter ? { status: statusFilter } : {};
      const response = await axios.get('/admin/gare', { params });
      setGare(response.data.competitions || []);
    } catch (error) {
      console.error('Error fetching gare:', error);
      toast.error('Errore nel caricamento delle gare');
    } finally {
      setLoading(false);
    }
  };

  const handleSuspend = async () => {
    if (!selectedGara || !suspendReason.trim()) return;
    setActionLoading(true);
    try {
      await axios.post(`/admin/gara/${selectedGara.competition_id}/suspend`, { reason: suspendReason });
      toast.success('Gara sospesa con successo');
      setShowSuspendDialog(false);
      setSuspendReason('');
      fetchGare();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella sospensione');
    } finally {
      setActionLoading(false);
    }
  };

  const handleResume = async (gara) => {
    setActionLoading(true);
    try {
      await axios.post(`/admin/gara/${gara.competition_id}/resume`, { extension_minutes: 30 });
      toast.success('Gara riattivata con successo');
      fetchGare();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella riattivazione');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!selectedGara || !cancelReason.trim()) return;
    setActionLoading(true);
    try {
      await axios.post(`/admin/gara/${selectedGara.competition_id}/cancel`, { reason: cancelReason });
      toast.success('Gara annullata');
      setShowCancelDialog(false);
      setCancelReason('');
      fetchGare();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'annullamento');
    } finally {
      setActionLoading(false);
    }
  };

  const formatPrice = (price) => {
    if (!price) return 'N/D';
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0 }).format(price);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      scheduled: { class: 'bg-blue-100 text-blue-800', label: 'Programmata' },
      active: { class: 'bg-green-100 text-green-800', label: 'In Corso' },
      suspended: { class: 'bg-red-100 text-red-800', label: 'Sospesa' },
      awarded: { class: 'bg-purple-100 text-purple-800', label: 'Aggiudicata' },
      under_review: { class: 'bg-orange-100 text-orange-800', label: 'In Revisione' },
      cancelled: { class: 'bg-gray-100 text-gray-800', label: 'Annullata' },
      not_awarded: { class: 'bg-gray-100 text-gray-800', label: 'Non Aggiudicata' }
    };
    const config = statusConfig[status] || { class: 'bg-gray-100', label: status };
    return <Badge className={config.class}>{config.label}</Badge>;
  };

  if (loading) return <div className="text-center py-8">Caricamento...</div>;

  return (
    <div className="space-y-6" data-testid="admin-gare-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900 ">Monitoraggio Gare di Vendita</h1>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="border rounded-lg px-4 py-2 text-sm"
        >
          <option value="">Tutti gli stati</option>
          <option value="active">In Corso</option>
          <option value="scheduled">Programmate</option>
          <option value="suspended">Sospese</option>
          <option value="awarded">Aggiudicate</option>
          <option value="under_review">In Revisione</option>
          <option value="cancelled">Annullate</option>
        </select>
      </div>

      {gare.length > 0 ? (
        <div className="grid gap-4">
          {gare.map((gara) => (
            <div key={gara.competition_id} className="bg-white rounded-lg border border-gray-100 p-6" data-testid={`admin-gara-${gara.competition_id}`}>
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="font-semibold text-lg text-gray-900">{gara.property?.address || 'Immobile'}</h3>
                  <p className="text-gray-500">{gara.property?.city}</p>
                  {gara.agent && (
                    <p className="text-sm text-gray-400 mt-1">
                      Agente: {gara.agent.first_name} {gara.agent.last_name} ({gara.agent.company_name})
                    </p>
                  )}
                </div>
                {getStatusBadge(gara.status)}
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm mb-4">
                <div>
                  <span className="text-gray-500">Valore Immobile</span>
                  <p className="font-semibold">{formatPrice(gara.valore_immobile)}</p>
                </div>
                <div>
                  <span className="text-gray-500">Base di Gara</span>
                  <p className="font-semibold text-[#0071e3]">{formatPrice(gara.base_di_gara)}</p>
                </div>
                <div>
                  <span className="text-gray-500">Prezzo Min. (nascosto)</span>
                  <p className="font-semibold text-orange-600">{formatPrice(gara.prezzo_minimo_aggiudicazione)}</p>
                </div>
                <div>
                  <span className="text-gray-500">Offerta Attuale</span>
                  <p className="font-semibold text-green-600">{formatPrice(gara.current_highest_bid) || '-'}</p>
                </div>
                <div>
                  <span className="text-gray-500">Offerte Totali</span>
                  <p className="font-semibold">{gara.total_bids || 0}</p>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 pt-4 border-t">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => navigate(`/gara/${gara.competition_id}`)}
                >
                  <Eye className="h-4 w-4 mr-2" />
                  Visualizza
                </Button>
                
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={async () => {
                    try {
                      const res = await axios.get(`/admin/gara/${gara.competition_id}/statistics`);
                      toast.info(`Statistiche: ${res.data.statistics?.total_bids || 0} offerte, ${res.data.statistics?.unique_bidders || 0} offerenti`);
                    } catch (e) {
                      toast.error('Errore caricamento statistiche');
                    }
                  }}
                >
                  <BarChart3 className="h-4 w-4 mr-2" />
                  Statistiche
                </Button>
                
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={async () => {
                    try {
                      const res = await axios.get(`/admin/gara/${gara.competition_id}/fraud-report`);
                      const level = res.data.risk_level;
                      toast[level === 'high' ? 'error' : level === 'medium' ? 'warning' : 'success'](
                        `Rischio: ${level.toUpperCase()} - ${res.data.suspicious_bidders} sospetti su ${res.data.total_bidders} offerenti`
                      );
                    } catch (e) {
                      toast.error('Errore caricamento report');
                    }
                  }}
                >
                  <AlertTriangle className="h-4 w-4 mr-2" />
                  Antifrode
                </Button>
                
                {gara.status === 'active' && (
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="border-red-300 text-red-600 hover:bg-red-50"
                    onClick={() => { setSelectedGara(gara); setShowSuspendDialog(true); }}
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    Sospendi
                  </Button>
                )}
                
                {gara.status === 'suspended' && (
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="border-green-300 text-green-600 hover:bg-green-50"
                    onClick={() => handleResume(gara)}
                    disabled={actionLoading}
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Riattiva
                  </Button>
                )}
                
                {!['awarded', 'cancelled'].includes(gara.status) && (
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="border-red-500 text-red-600 hover:bg-red-50"
                    onClick={() => { setSelectedGara(gara); setShowCancelDialog(true); }}
                  >
                    Annulla Gara
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-100 p-8 text-center">
          <Gavel className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">Nessuna gara di vendita trovata</p>
        </div>
      )}

      {/* Suspend Dialog */}
      <Dialog open={showSuspendDialog} onOpenChange={setShowSuspendDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Sospendi Gara</DialogTitle>
            <DialogDescription>
              Inserisci il motivo della sospensione. La gara potrà essere riattivata successivamente.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <textarea
              value={suspendReason}
              onChange={(e) => setSuspendReason(e.target.value)}
              placeholder="Motivo della sospensione..."
              className="w-full border rounded-lg p-3 min-h-[100px]"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSuspendDialog(false)}>Annulla</Button>
            <Button 
              onClick={handleSuspend} 
              disabled={actionLoading || !suspendReason.trim()}
              className="bg-red-600 hover:bg-red-700"
            >
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Sospendi Gara
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Dialog */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">Annulla Gara Definitivamente</DialogTitle>
            <DialogDescription>
              Questa azione è irreversibile. Tutte le offerte saranno invalidate.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Motivo dell'annullamento..."
              className="w-full border rounded-lg p-3 min-h-[100px]"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCancelDialog(false)}>Annulla</Button>
            <Button 
              onClick={handleCancel} 
              disabled={actionLoading || !cancelReason.trim()}
              className="bg-red-600 hover:bg-red-700"
            >
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Conferma Annullamento
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Analytics Page
const AnalyticsPage = () => {
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [trends, setTrends] = useState(null);
  const [topAgents, setTopAgents] = useState([]);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [overviewRes, trendsRes, agentsRes] = await Promise.all([
        axios.get('/admin/analytics/overview'),
        axios.get('/admin/analytics/trends?days=30'),
        axios.get('/admin/analytics/top-agents?limit=5')
      ]);
      
      setOverview(overviewRes.data);
      setTrends(trendsRes.data);
      setTopAgents(agentsRes.data.top_agents);
    } catch (error) {
      toast.error('Errore nel caricamento analytics');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAllData();
    setRefreshing(false);
    toast.success('Dati aggiornati');
  };

  const ProgressBar = ({ value, max, color = "bg-[#0071e3]" }) => (
    <div className="w-full bg-gray-200 rounded-full h-2">
      <div 
        className={`h-2 rounded-full ${color}`}
        style={{ width: `${Math.min((value / max) * 100, 100)}%` }}
      />
    </div>
  );

  const getTrendTotal = (trendArray) => {
    if (!trendArray || trendArray.length === 0) return 0;
    return trendArray.reduce((sum, item) => sum + (item.count || item.total || 0), 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0071e3]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900 ">Analytics Dashboard</h1>
        <Button onClick={handleRefresh} disabled={refreshing} variant="outline" size="sm">
          <BarChart3 className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Aggiorna
        </Button>
      </div>

      {/* User Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg border p-4">
          <p className="text-sm text-gray-500">Proprietari</p>
          <p className="text-2xl font-bold text-blue-600">{overview?.total_users?.sellers || 0}</p>
          <p className="text-xs text-gray-400">+{overview?.new_users_30d?.sellers || 0} ultimi 30gg</p>
        </div>
        <div className="bg-white rounded-lg border p-4">
          <p className="text-sm text-gray-500">Agenti</p>
          <p className="text-2xl font-bold text-green-600">{overview?.total_users?.agents || 0}</p>
          <p className="text-xs text-gray-400">+{overview?.new_users_30d?.agents || 0} ultimi 30gg</p>
        </div>
        <div className="bg-white rounded-lg border p-4">
          <p className="text-sm text-gray-500">Acquirenti</p>
          <p className="text-2xl font-bold text-purple-600">{overview?.total_users?.buyers || 0}</p>
          <p className="text-xs text-gray-400">+{overview?.new_users_30d?.buyers || 0} ultimi 30gg</p>
        </div>
        <div className="bg-white rounded-lg border p-4">
          <p className="text-sm text-gray-500">Totale Utenti</p>
          <p className="text-2xl font-bold text-[#0071e3]">
            {(overview?.total_users?.sellers || 0) + (overview?.total_users?.agents || 0) + (overview?.total_users?.buyers || 0)}
          </p>
        </div>
      </div>

      {/* Property & Auction */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg border p-6">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Building className="h-5 w-5" /> Immobili
          </h3>
          <div className="space-y-4">
            <div className="flex justify-between">
              <span>Totale</span>
              <span className="font-bold">{overview?.property_stats?.total_seller_properties || 0}</span>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-amber-600">In revisione</span>
                <span>{overview?.property_stats?.pending_review || 0}</span>
              </div>
              <ProgressBar value={overview?.property_stats?.pending_review || 0} max={overview?.property_stats?.total_seller_properties || 1} color="bg-amber-500" />
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-green-600">Approvati</span>
                <span>{overview?.property_stats?.approved || 0}</span>
              </div>
              <ProgressBar value={overview?.property_stats?.approved || 0} max={overview?.property_stats?.total_seller_properties || 1} color="bg-green-500" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border p-6">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Gavel className="h-5 w-5" /> Aste
          </h3>
          <div className="grid grid-cols-2 gap-3">
            <div className="text-center p-3 bg-green-50 rounded-lg">
              <p className="text-xl font-bold text-green-600">{overview?.auction_stats?.active || 0}</p>
              <p className="text-xs text-gray-600">Attive</p>
            </div>
            <div className="text-center p-3 bg-blue-50 rounded-lg">
              <p className="text-xl font-bold text-blue-600">{overview?.auction_stats?.scheduled || 0}</p>
              <p className="text-xs text-gray-600">Programmate</p>
            </div>
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <p className="text-xl font-bold text-gray-600">{overview?.auction_stats?.completed || 0}</p>
              <p className="text-xs text-gray-600">Completate</p>
            </div>
            <div className="text-center p-3 bg-[#0071e3]/10 rounded-lg">
              <p className="text-xl font-bold text-[#0071e3]">{overview?.auction_stats?.total_bids || 0}</p>
              <p className="text-xs text-gray-600">Offerte totali</p>
            </div>
          </div>
        </div>
      </div>

      {/* Conversion Rates */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg border p-6">
          <h3 className="font-semibold mb-4">Proposte</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span>Totale</span><span className="font-bold">{overview?.proposal_stats?.total_proposals || 0}</span></div>
            <div className="flex justify-between text-amber-600"><span>In attesa</span><span>{overview?.proposal_stats?.pending || 0}</span></div>
            <div className="flex justify-between text-green-600"><span>Accettate</span><span>{overview?.proposal_stats?.accepted || 0}</span></div>
          </div>
        </div>
        <div className="bg-white rounded-lg border p-6">
          <h3 className="font-semibold mb-4">Conversioni</h3>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span>Proposta → Mandato</span>
                <span className="font-bold">{overview?.conversion_rates?.proposal_to_mandate || 0}%</span>
              </div>
              <ProgressBar value={overview?.conversion_rates?.proposal_to_mandate || 0} max={100} />
            </div>
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span>Immobile → Gara</span>
                <span className="font-bold">{overview?.conversion_rates?.property_to_auction || 0}%</span>
              </div>
              <ProgressBar value={overview?.conversion_rates?.property_to_auction || 0} max={100} />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border p-6">
          <h3 className="font-semibold mb-4">Mandati</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span>Totale</span><span className="font-bold">{overview?.mandate_stats?.total_mandates || 0}</span></div>
            <div className="flex justify-between text-green-600"><span>Firmati</span><span>{overview?.mandate_stats?.signed || 0}</span></div>
            <div className="flex justify-between text-amber-600"><span>In attesa firma</span><span>{overview?.mandate_stats?.pending_signature || 0}</span></div>
          </div>
        </div>
      </div>

      {/* Top Agents */}
      <div className="bg-white rounded-lg border p-6">
        <h3 className="font-semibold mb-4">Top Agenti</h3>
        <div className="space-y-3">
          {topAgents.map((agent, idx) => (
            <div key={agent.agent_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center font-bold text-white text-sm ${
                  idx === 0 ? 'bg-[#0071e3]' : idx === 1 ? 'bg-gray-400' : idx === 2 ? 'bg-amber-600' : 'bg-gray-300'
                }`}>{idx + 1}</div>
                <div>
                  <p className="font-medium text-sm">{agent.company_name || agent.name}</p>
                  <p className="text-xs text-gray-500">{agent.name}</p>
                </div>
              </div>
              <div className="flex items-center gap-4 text-xs">
                <div className="text-center">
                  <p className="font-bold text-[#0071e3]">{agent.performance?.mandates_won || 0}</p>
                  <p className="text-gray-500">Mandati</p>
                </div>
                <div className="text-center">
                  <p className="font-bold text-green-600">{agent.performance?.acceptance_rate || 0}%</p>
                  <p className="text-gray-500">Acc.</p>
                </div>
                <Badge className="bg-[#0071e3] text-xs">{agent.composite_score}</Badge>
              </div>
            </div>
          ))}
          {topAgents.length === 0 && <p className="text-center text-gray-500 py-4">Nessun agente trovato</p>}
        </div>
      </div>

      {/* Trends */}
      {trends && (
        <div className="bg-white rounded-lg border p-6">
          <h3 className="font-semibold mb-4">Trend Ultimi 30 Giorni</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 bg-gray-50 rounded-lg text-center">
              <p className="text-2xl font-bold">{getTrendTotal(trends.daily_registrations)}</p>
              <p className="text-xs text-gray-500">Nuove Registrazioni</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg text-center">
              <p className="text-2xl font-bold">{getTrendTotal(trends.daily_properties)}</p>
              <p className="text-xs text-gray-500">Nuovi Immobili</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg text-center">
              <p className="text-2xl font-bold">{getTrendTotal(trends.daily_bids)}</p>
              <p className="text-xs text-gray-500">Offerte</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg text-center">
              <p className="text-2xl font-bold">{getTrendTotal(trends.daily_proposals)}</p>
              <p className="text-xs text-gray-500">Proposte</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Sellers Management Page
const SellersManagementPage = () => {
  const [sellers, setSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedSeller, setSelectedSeller] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [verifyDialogOpen, setVerifyDialogOpen] = useState(false);
  const [verificationNotes, setVerificationNotes] = useState('');

  useEffect(() => {
    fetchSellers();
  }, [statusFilter, searchTerm]);

  const fetchSellers = async () => {
    try {
      setLoading(true);
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (searchTerm) params.search = searchTerm;
      
      const response = await axios.get('/admin/sellers', { params });
      setSellers(response.data);
    } catch (error) {
      console.error('Error fetching sellers:', error);
      toast.error('Errore nel caricamento dei proprietari');
    } finally {
      setLoading(false);
    }
  };

  const openSellerDetail = async (sellerId) => {
    try {
      const response = await axios.get(`/admin/sellers/${sellerId}`);
      setSelectedSeller(response.data);
      setDetailDialogOpen(true);
    } catch (error) {
      toast.error('Errore nel caricamento dei dettagli');
    }
  };

  const updateSellerStatus = async (sellerId, newStatus) => {
    try {
      await axios.put(`/admin/sellers/${sellerId}/status`, null, {
        params: { status: newStatus }
      });
      toast.success(`Stato aggiornato a: ${newStatus}`);
      fetchSellers();
      if (selectedSeller) {
        setSelectedSeller(prev => ({
          ...prev,
          seller: { ...prev.seller, account_status: newStatus }
        }));
      }
    } catch (error) {
      toast.error('Errore nell\'aggiornamento dello stato');
    }
  };

  const verifyDocument = async (sellerId, verified) => {
    try {
      await axios.put(`/admin/sellers/${sellerId}/verify-document`, null, {
        params: { verified, notes: verificationNotes }
      });
      toast.success(verified ? 'Documento verificato' : 'Documento rifiutato');
      setVerifyDialogOpen(false);
      setVerificationNotes('');
      fetchSellers();
      if (selectedSeller) {
        openSellerDetail(sellerId);
      }
    } catch (error) {
      toast.error('Errore nella verifica del documento');
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'registrato': { label: 'Registrato', class: 'bg-gray-100 text-gray-800' },
      'email_verificata': { label: 'Email Verificata', class: 'bg-blue-100 text-blue-800' },
      'telefono_verificato': { label: 'Telefono Verificato', class: 'bg-blue-100 text-blue-800' },
      'documento_in_verifica': { label: 'Doc. in Verifica', class: 'bg-yellow-100 text-yellow-800' },
      'verificato': { label: 'Verificato', class: 'bg-green-100 text-green-800' },
      'sospeso': { label: 'Sospeso', class: 'bg-red-100 text-red-800' }
    };
    const s = statusMap[status] || { label: status || 'Registrato', class: 'bg-gray-100 text-gray-800' };
    return <Badge className={s.class}>{s.label}</Badge>;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900 ">Gestione Proprietari</h1>
        <Badge className="bg-[#0071e3] text-white">{sellers.length} totali</Badge>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-100 p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Cerca per nome, email, codice fiscale..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Stato account" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli stati</SelectItem>
              <SelectItem value="registrato">Registrato</SelectItem>
              <SelectItem value="documento_in_verifica">Doc. in Verifica</SelectItem>
              <SelectItem value="verificato">Verificato</SelectItem>
              <SelectItem value="sospeso">Sospeso</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Sellers Table */}
      {loading ? (
        <div className="bg-white rounded-lg border border-gray-100 p-8 text-center">
          <div className="animate-pulse">Caricamento...</div>
        </div>
      ) : sellers.length > 0 ? (
        <div className="bg-white rounded-lg border border-gray-100 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Proprietario</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Codice Fiscale</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stato</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Immobili</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {sellers.map((seller) => (
                <tr key={seller.user_id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div>
                      <p className="font-medium text-gray-900">{seller.first_name} {seller.last_name}</p>
                      <p className="text-sm text-gray-500">{seller.email}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">{seller.tax_code}</td>
                  <td className="px-6 py-4">{getStatusBadge(seller.account_status)}</td>
                  <td className="px-6 py-4">
                    <Badge variant="outline">{seller.properties_count || 0} immobili</Badge>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openSellerDetail(seller.user_id)}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        Dettagli
                      </Button>
                      {seller.account_status === 'documento_in_verifica' && (
                        <Button
                          size="sm"
                          className="bg-[#0071e3] hover:bg-[#005BB5] text-white"
                          onClick={() => {
                            setSelectedSeller({ seller });
                            setVerifyDialogOpen(true);
                          }}
                        >
                          <Shield className="h-4 w-4 mr-1" />
                          Verifica
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-100 p-8 text-center">
          <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">Nessun proprietario trovato</p>
        </div>
      )}

      {/* Seller Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <User className="h-5 w-5 text-[#0071e3]" />
              <span>Dettaglio Proprietario</span>
            </DialogTitle>
          </DialogHeader>
          
          {selectedSeller && (
            <div className="space-y-6">
              {/* Personal Info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-3">Dati Personali</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Nome:</span>
                    <p className="font-medium">{selectedSeller.seller?.first_name} {selectedSeller.seller?.last_name}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Email:</span>
                    <p className="font-medium">{selectedSeller.seller?.email}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Telefono:</span>
                    <p className="font-medium">{selectedSeller.seller?.phone}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Codice Fiscale:</span>
                    <p className="font-medium">{selectedSeller.seller?.tax_code}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Stato Account:</span>
                    <div className="mt-1">{getStatusBadge(selectedSeller.seller?.account_status)}</div>
                  </div>
                  <div>
                    <span className="text-gray-500">Identità Verificata:</span>
                    <p className="font-medium">
                      {selectedSeller.seller?.identity_verified ? (
                        <span className="text-green-600 flex items-center">
                          <CheckCircle className="h-4 w-4 mr-1" /> Sì
                        </span>
                      ) : (
                        <span className="text-yellow-600 flex items-center">
                          <Clock className="h-4 w-4 mr-1" /> In attesa
                        </span>
                      )}
                    </p>
                  </div>
                </div>
              </div>

              {/* Properties */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Immobili Inseriti ({selectedSeller.properties?.length || 0})</h3>
                {selectedSeller.properties?.length > 0 ? (
                  <div className="space-y-3">
                    {selectedSeller.properties.map((prop) => (
                      <div key={prop.property_id} className="border rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">{prop.address}</p>
                            <p className="text-sm text-gray-500">{prop.city} ({prop.province})</p>
                          </div>
                          <div className="text-right">
                            <Badge variant="outline">{prop.status}</Badge>
                            <div className="mt-2">
                              <Progress value={prop.document_completion_percentage || 0} className="h-2 w-24" />
                              <span className="text-xs text-gray-500">{prop.document_completion_percentage || 0}% documenti</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">Nessun immobile inserito</p>
                )}
              </div>

              {/* Proposals */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Proposte Ricevute ({selectedSeller.proposals?.length || 0})</h3>
                {selectedSeller.proposals?.length > 0 ? (
                  <div className="space-y-2">
                    {selectedSeller.proposals.map((prop) => (
                      <div key={prop.proposal_id} className="border rounded-lg p-3 text-sm">
                        <div className="flex justify-between">
                          <span>Proposta da agente {prop.agent_id}</span>
                          <Badge variant="outline">{prop.status}</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">Nessuna proposta</p>
                )}
              </div>

              {/* Mandates */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Mandati Firmati ({selectedSeller.mandates?.length || 0})</h3>
                {selectedSeller.mandates?.length > 0 ? (
                  <div className="space-y-2">
                    {selectedSeller.mandates.map((mandate) => (
                      <div key={mandate.mandate_id} className="border rounded-lg p-3 text-sm">
                        <div className="flex justify-between">
                          <span>Mandato {mandate.mandate_id}</span>
                          <Badge className="bg-green-100 text-green-800">{mandate.status}</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">Nessun mandato firmato</p>
                )}
              </div>

              {/* Actions */}
              <div className="flex justify-end space-x-3 pt-4 border-t">
                <Select 
                  value={selectedSeller.seller?.account_status}
                  onValueChange={(value) => updateSellerStatus(selectedSeller.seller?.user_id, value)}
                >
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="Cambia stato" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="registrato">Registrato</SelectItem>
                    <SelectItem value="documento_in_verifica">Doc. in Verifica</SelectItem>
                    <SelectItem value="verificato">Verificato</SelectItem>
                    <SelectItem value="sospeso">Sospeso</SelectItem>
                  </SelectContent>
                </Select>
                {!selectedSeller.seller?.identity_verified && (
                  <Button
                    className="bg-[#0071e3] hover:bg-[#005BB5] text-white"
                    onClick={() => setVerifyDialogOpen(true)}
                  >
                    <Shield className="h-4 w-4 mr-2" />
                    Verifica Documento
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Verify Document Dialog */}
      <Dialog open={verifyDialogOpen} onOpenChange={setVerifyDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Verifica Documento di Identità</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Conferma la verifica del documento di identità per {selectedSeller?.seller?.first_name} {selectedSeller?.seller?.last_name}
            </p>
            
            <div>
              <Label htmlFor="notes">Note (opzionale)</Label>
              <Textarea
                id="notes"
                placeholder="Inserisci eventuali note sulla verifica..."
                value={verificationNotes}
                onChange={(e) => setVerificationNotes(e.target.value)}
              />
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => verifyDocument(selectedSeller?.seller?.user_id, false)}
                className="text-red-600"
              >
                <X className="h-4 w-4 mr-2" />
                Rifiuta
              </Button>
              <Button
                onClick={() => verifyDocument(selectedSeller?.seller?.user_id, true)}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                <Check className="h-4 w-4 mr-2" />
                Approva
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Main Admin Dashboard
const AdminDashboard = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { token } = useAuth();

  return (
    <div className="min-h-screen bg-[#F9F9F7]">
      <Sidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
      
      <div className="lg:hidden sticky top-0 z-30 bg-[#0071e3] px-4 py-3">
        <div className="flex items-center justify-between">
          <button onClick={() => setSidebarOpen(true)} className="text-white">
            <Menu className="h-6 w-6" />
          </button>
          <span className="font-bold text-white ">WEQASA Admin</span>
          <div className="w-6" />
        </div>
      </div>

      <div className="lg:ml-64 p-6">
        <Routes>
          <Route index element={<AdminOverview />} />
          <Route path="agenti" element={<AgentsManagementPage />} />
          <Route path="acquirenti" element={<BuyersManagementPage />} />
          <Route path="proprietari" element={<SellersManagementPage />} />
          <Route path="immobili-agenti" element={<PropertiesManagementPage />} />
          <Route path="immobili-proprietari" element={<SellerPropertiesManagementPage />} />
          <Route path="scout" element={<PrivateListingScout />} />
          <Route path="gare" element={<AuctionsMonitoringPage />} />
          <Route path="analytics" element={<AnalyticsPage />} />
          <Route path="sicurezza" element={<AuditLogsPage token={token} />} />
        </Routes>
      </div>
    </div>
  );
};

export default AdminDashboard;
